import * as React from "react";
import { useHistory } from "react-router-dom";
import { useMappedState } from "redux-react-hook";

import Table from "src/core/dashboard/components/widgets/table/Table";
import type { IStore } from "src/core/shared/store";
import { Button } from "src/library/components/Button";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import {
  Transaction,
  useAccountingApi
} from "src/core/accounting/api/accounting.api";
import { transactionTableColumns } from "src/core/shared/components/tables/transactionTableColumns";
import { useDrop } from "src/core/drops/hooks";

interface RelatedTransactionsTabProps {
  /** Transaction data */
  invoiceId?: number;
}

const defaultPagination: PaginatedParams<Transaction> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "transactionDate", "DESC" ] ]
};

/**
 * Related Transactions Tab - displays transactions in a table format
 * Used in: InvoiceDetailsPage
 */
const RelatedTransactionsTab: React.FC<RelatedTransactionsTabProps> = ({ invoiceId }) => {
  const history = useHistory();
  const [ { drop } ] = useDrop();
  const [ getTransactionsRes, getTransactionsApi ] = useAccountingApi(drop?.customerId ? "LIST_CUSTOMER_TRANSACTIONS" : "LIST_TRANSACTIONS");

  const onFetchData = React.useCallback(
    (params: { filters: FilterParams<Transaction>; pagination: PaginatedParams<Transaction> }) => {
      if (drop?.id) {
        getTransactionsApi({
          customerId: drop.customerId,
          filters: {
            ...params.filters,
            invoiceId
          },
          pagination: params.pagination
        });
      } else {
        getTransactionsApi({
          filters: {
            ...params.filters,
            invoiceId
          },
          pagination: params.pagination
        });
      }
    },
    [
      drop?.id,
      drop?.customerId,
      getTransactionsApi,
      invoiceId
    ],
  );

  // Get current role for role-based navigation
  const { currentRole } = useMappedState(({ global }: IStore) => ({ currentRole: global.role }));
  const isAdmin = currentRole?.type === "admin";

  // Action button for each row
  const renderActionButton = React.useCallback(
    (info: { row: { original: Transaction } }) => {
      const transaction = info.row.original;
      // Use role-based URL: admin uses /admin/accounting/transactions, customer uses /accounting/transactions
      const transactionUrl = isAdmin ? `/admin/accounting/transactions/${transaction.id}` : `/accounting/transactions/${transaction.id}`;

      return (
        <Button
          $buttonType="primary"
          onClick={() => history.push(transactionUrl)}
        >
          View Transaction
        </Button>
      );
    },
    [ history, isAdmin ],
  );

  // Build columns with action button
  // Disable filtering on all columns - this is static data (following RelatedOrdersTab pattern)
  const columns = React.useMemo(() => {
    return transactionTableColumns({
      showActions: true,
      actionButton: renderActionButton,
      showMatchedValue: true // Show matched value column for invoice-transaction relationship
    });
  }, [ renderActionButton ]);

  return (
    <Table
      tableOf="Transactions"
      data={getTransactionsRes.data?.transactions.result || []}
      columns={columns}
      loading={getTransactionsRes.pending}
      onFetchData={onFetchData}
      defaultPagination={defaultPagination}
      pageCount={getTransactionsRes.data?.transactions.pages}
      rowCount={getTransactionsRes.data?.transactions.count}
    />
  );
};

export default RelatedTransactionsTab;
